import { useState, type ReactNode } from "react";
import { Card } from "../lib/ui";
import {
  Send,
  Inbox,
  CheckCheck,
  Eye,
  Reply,
  Timer,
  Zap,
  Repeat,
  CalendarCheck,
  Hand,
  ThumbsUp,
  AlertTriangle,
  VolumeX,
  Table2,
  BarChart3,
  type LucideIcon,
} from "lucide-react";

/**
 * As abas de análise detalhada: mensagens, follow-up, corretor e IA.
 *
 * Os números vêm de `/api/analise/detalhe`. Gráfico só onde há forma para
 * ver -- volume ao longo dos dias, hora do dia, custo por modelo. O resto é
 * número solto ou tabela, porque um gráfico de uma barra é só um número
 * desenhado grande.
 *
 * Cores das séries: as três primeiras da paleta categórica, validadas juntas
 * contra o fundo do painel (#111817) para daltonismo e contraste. A cor segue
 * a série e nunca a posição: "IA" é sempre laranja, mesmo quando o corretor
 * não escreveu nada no período.
 */

export const SERIE = {
  azul: "#3987e5",
  laranja: "#d95926",
  verde: "#199e70",
} as const;

// ------------------------------------------------------------------- tipos

export interface AnaliseDetalhada {
  dias: number;
  mensagens: {
    por_dia: { dia: string; lead: number; ia: number; corretor: number }[];
    recebidas: number;
    enviadas_ia: number;
    enviadas_corretor: number;
    recibos_desde: string | null;
    saidas_medidas: number;
    entregues: number;
    lidas: number;
    conversas_com_envio: number;
    conversas_com_resposta: number;
    retorno_mediana_min: number | null;
    resposta_ia_mediana_s: number | null;
    por_hora: { hora: number; lead: number }[];
    abordagens: { status: string; quantos: number }[];
    motivos_recusa: { motivo: string; quantos: number }[];
  };
  followup: {
    enviados: number;
    responderam: number;
    agendaram_depois: number;
    pulados: number;
    falhos: number;
    agendados_agora: number;
    por_tentativa: { tentativa: number; enviados: number; responderam: number }[];
    por_etapa: { etapa: string; enviados: number; responderam: number }[];
    cancelados: { motivo: string; quantos: number }[];
  };
  escalonamentos: {
    total: number;
    assumidos: number;
    assumir_mediana_min: number | null;
    justos: number;
    injustos: number;
    por_urgencia: { urgencia: string; quantos: number }[];
    motivos: { motivo: string; quantos: number }[];
  };
  ia: {
    custo_por_dia: { dia: string; modelo: string; usd: number; respostas: number }[];
    ferramentas: { nome: string; usos: number; turnos: number }[];
    respostas: number;
    com_erro: number;
    silencios: number;
    escalaram: number;
    duracao_mediana_ms: number | null;
  };
}

// ----------------------------------------------------------------- formatos

const pct = (parte: number, todo: number) =>
  todo > 0 ? `${Math.round((100 * parte) / todo)}%` : "—";

const dm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

function minutos(min: number | null): string {
  if (min === null) return "—";
  if (min < 60) return `${min} min`;
  const h = min / 60;
  if (h < 48) return `${h < 10 ? h.toFixed(1).replace(".", ",") : Math.round(h)} h`;
  return `${Math.round(h / 24)} dias`;
}

function segundos(s: number | null): string {
  if (s === null) return "—";
  if (s < 90) return `${s} s`;
  return minutos(Math.round(s / 60));
}

const ROTULO_MODELO: Record<string, string> = {
  "claude-opus-5": "Opus 5",
  "claude-sonnet-5": "Sonnet 5",
};

const ROTULO_ETAPA: Record<string, string> = {
  novo: "Novo",
  engajado: "Engajado",
  descoberta: "Descoberta",
  qualificado: "Qualificado",
  match: "Match",
  visita_agendada: "Visita agendada",
  visita_realizada: "Visita realizada",
  proposta: "Proposta",
  ganho: "Ganho",
  perdido: "Perdido",
};

const ROTULO_ABORDAGEM: Record<string, string> = {
  enviada: "Enviadas",
  recusada: "Não enviadas",
  falhou: "Falharam",
  pendente: "Na fila",
};

const ROTULO_FERRAMENTA: Record<string, string> = {
  consultar_base: "Consultar a base",
  registrar_fato: "Registrar fato",
  horarios_livres: "Ver horários livres",
  agendar_visita: "Agendar",
  cancelar_agendamento: "Cancelar agendamento",
  escalar_humano: "Chamar o corretor",
  buscar_imoveis: "Buscar imóveis",
  enviar_imovel: "Enviar imóvel",
  enviar_fotos: "Enviar fotos",
};

// ------------------------------------------------------------ peças comuns

export function Kpi({
  rotulo,
  valor,
  ajuda,
  icone: Icone,
  tom,
}: {
  rotulo: string;
  valor: ReactNode;
  ajuda?: ReactNode;
  icone?: LucideIcon;
  tom?: "ok" | "warm" | "danger";
}) {
  const cor =
    tom === "ok"
      ? "bg-ok-dim text-ok"
      : tom === "warm"
        ? "bg-warm-dim text-warm"
        : tom === "danger"
          ? "bg-danger-dim text-danger"
          : "bg-accent-dim text-accent";
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2">
        {Icone && (
          <span className={`w-6 h-6 rounded-md grid place-items-center shrink-0 ${cor}`}>
            <Icone size={14} strokeWidth={2.2} />
          </span>
        )}
        <div className="text-xs font-medium text-ink-3">{rotulo}</div>
      </div>
      <div className="text-2xl font-semibold tracking-tight text-ink mt-3">{valor}</div>
      {ajuda && <div className="text-xs text-ink-3 mt-1.5 leading-snug">{ajuda}</div>}
    </Card>
  );
}

function Titulo({ children, nota }: { children: ReactNode; nota?: ReactNode }) {
  return (
    <div className="mb-3">
      <h2 className="text-sm font-semibold text-ink">{children}</h2>
      {nota && <p className="text-xs text-ink-3 mt-0.5 max-w-prose">{nota}</p>}
    </div>
  );
}

/** Lista de motivos com barra fina: o maior define a escala. */
function Ranking({
  itens,
  vazio,
}: {
  itens: { rotulo: string; valor: number }[];
  vazio: string;
}) {
  if (itens.length === 0) return <p className="text-xs text-ink-3">{vazio}</p>;
  const maior = Math.max(...itens.map((i) => i.valor), 1);
  return (
    <div className="space-y-2.5">
      {itens.map((i) => (
        <div key={i.rotulo}>
          <div className="flex justify-between gap-3 text-xs">
            <span className="text-ink-2 truncate" title={i.rotulo}>
              {i.rotulo}
            </span>
            <span className="text-ink tabular-nums">{i.valor}</span>
          </div>
          <div className="h-1.5 rounded-full bg-surface-3 mt-1 overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{ width: `${(100 * i.valor) / maior}%`, background: SERIE.azul }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------ barras em pilha

interface Serie {
  id: string;
  rotulo: string;
  cor: string;
}

/**
 * Colunas empilhadas por dia (ou por hora), com tooltip e vista em tabela.
 *
 * Feito à mão em vez de biblioteca: são dezenas de divs, e uma lib de gráfico
 * pesaria mais que o painel inteiro. Barras finas com 2px de folga entre
 * fatias; a ponta de cima arredondada, a base reta na linha do zero.
 *
 * O tooltip mostra TODAS as séries da coluna, com o valor em destaque -- quem
 * passa o mouse já sabe a série pela legenda e quer o número. O mesmo aparece
 * no foco do teclado, e a tabela deixa tudo legível sem mouse nenhum.
 */
export function BarrasEmPilha({
  linhas,
  series,
  rotulo,
  formato = (n) => String(n),
  altura = 160,
  vazio,
}: {
  linhas: { chave: string; valores: Record<string, number> }[];
  series: Serie[];
  rotulo: (chave: string) => string;
  formato?: (n: number) => string;
  altura?: number;
  vazio?: string;
}) {
  const [foco, setFoco] = useState<number | null>(null);
  const [tabela, setTabela] = useState(false);

  const total = (v: Record<string, number>) =>
    series.reduce((s, x) => s + (v[x.id] ?? 0), 0);
  const maior = Math.max(...linhas.map((l) => total(l.valores)), 0);

  if (maior === 0) {
    return <p className="text-xs text-ink-3 py-6 text-center">{vazio ?? "Nada no período."}</p>;
  }

  // Três marcas no eixo: zero, metade e o topo arredondado para cima.
  const topo = arredondaTopo(maior);
  const marcas = [topo, topo / 2, 0];
  // Poucos rótulos no eixo: no celular, "17/08 21/08" já encosta um no outro.
  const passoRotulo = Math.max(1, Math.ceil(linhas.length / 6));

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        {series.length > 1 ? (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-2">
            {series.map((s) => (
              <span key={s.id} className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-[3px]" style={{ background: s.cor }} />
                {s.rotulo}
              </span>
            ))}
          </div>
        ) : (
          <span />
        )}
        <button
          onClick={() => setTabela((x) => !x)}
          className="inline-flex items-center gap-1.5 text-xs text-ink-3 hover:text-ink transition-colors"
        >
          {tabela ? <BarChart3 size={13} /> : <Table2 size={13} />}
          {tabela ? "Gráfico" : "Tabela"}
        </button>
      </div>

      {tabela ? (
        <div className="overflow-x-auto max-h-72 overflow-y-auto rounded-lg border border-rule">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-surface-2">
              <tr className="text-ink-3">
                <th className="text-left font-medium px-3 py-2"> </th>
                {series.map((s) => (
                  <th key={s.id} className="text-right font-medium px-3 py-2">
                    {s.rotulo}
                  </th>
                ))}
                {series.length > 1 && (
                  <th className="text-right font-medium px-3 py-2">Total</th>
                )}
              </tr>
            </thead>
            <tbody>
              {linhas.map((l) => (
                <tr key={l.chave} className="border-t border-rule">
                  <td className="px-3 py-1.5 text-ink-2 tabular-nums">{rotulo(l.chave)}</td>
                  {series.map((s) => (
                    <td key={s.id} className="px-3 py-1.5 text-right tabular-nums text-ink-2">
                      {formato(l.valores[s.id] ?? 0)}
                    </td>
                  ))}
                  {series.length > 1 && (
                    <td className="px-3 py-1.5 text-right tabular-nums text-ink">
                      {formato(total(l.valores))}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex gap-2">
          {/* Eixo: só três números, em hairline. */}
          <div
            className="flex flex-col justify-between text-[10px] text-ink-3 tabular-nums text-right shrink-0"
            style={{ height: altura }}
          >
            {marcas.map((m) => (
              <span key={m} className="leading-none -translate-y-[1px]">
                {formato(m)}
              </span>
            ))}
          </div>

          <div className="flex-1 min-w-0">
            <div className="relative" style={{ height: altura }}>
              {marcas.map((m) => (
                <div
                  key={m}
                  className={`absolute inset-x-0 h-px ${m === 0 ? "bg-rule-2" : "bg-rule"}`}
                  style={{ top: `${(1 - m / topo) * 100}%` }}
                />
              ))}

              <div className="absolute inset-0 flex items-end gap-[2px]">
                {linhas.map((l, i) => {
                  const t = total(l.valores);
                  const ativo = foco === i;
                  return (
                    <div
                      key={l.chave}
                      tabIndex={0}
                      aria-label={`${rotulo(l.chave)}: ${series
                        .map((s) => `${s.rotulo} ${formato(l.valores[s.id] ?? 0)}`)
                        .join(", ")}`}
                      onPointerEnter={() => setFoco(i)}
                      onPointerLeave={() => setFoco((f) => (f === i ? null : f))}
                      onFocus={() => setFoco(i)}
                      onBlur={() => setFoco((f) => (f === i ? null : f))}
                      className="relative flex-1 min-w-0 h-full flex flex-col justify-end items-center outline-none"
                    >
                      {/* A área de toque é a coluna inteira; a barra é só a tinta. */}
                      <div
                        className={`w-full max-w-6 flex flex-col-reverse gap-[2px] transition-[filter] ${
                          ativo ? "brightness-125" : ""
                        }`}
                        style={{ height: `${(t / topo) * 100}%` }}
                      >
                        {series.map((s, k) => {
                          const v = l.valores[s.id] ?? 0;
                          if (v <= 0) return null;
                          // A última fatia com valor é a do topo: só ela arredonda.
                          const ehTopo = series.slice(k + 1).every((x) => !((l.valores[x.id] ?? 0) > 0));
                          return (
                            <div
                              key={s.id}
                              style={{
                                flexGrow: v,
                                flexBasis: 0,
                                minHeight: 2,
                                background: s.cor,
                                borderRadius: ehTopo ? "4px 4px 0 0" : 0,
                              }}
                            />
                          );
                        })}
                      </div>

                      {ativo && (
                        <Dica
                          titulo={rotulo(l.chave)}
                          lado={i > linhas.length / 2 ? "esquerda" : "direita"}
                          linhas={series.map((s) => ({
                            cor: s.cor,
                            rotulo: s.rotulo,
                            valor: formato(l.valores[s.id] ?? 0),
                          }))}
                          total={series.length > 1 ? formato(t) : undefined}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-[2px] mt-1.5 text-[10px] text-ink-3 tabular-nums">
              {linhas.map((l, i) => (
                <div key={l.chave} className="flex-1 min-w-0 text-center overflow-visible whitespace-nowrap">
                  {i % passoRotulo === 0 ? rotulo(l.chave) : ""}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function arredondaTopo(n: number): number {
  if (n <= 0) return 1;
  const ordem = 10 ** Math.floor(Math.log10(n));
  for (const m of [1, 2, 2.5, 5, 10]) {
    if (m * ordem >= n) return m * ordem;
  }
  return 10 * ordem;
}

function Dica({
  titulo,
  linhas,
  total,
  lado,
}: {
  titulo: string;
  linhas: { cor: string; rotulo: string; valor: string }[];
  total?: string;
  lado: "esquerda" | "direita";
}) {
  return (
    <div
      role="tooltip"
      className={`absolute top-0 z-20 pointer-events-none min-w-36 rounded-lg bg-surface-2 px-3 py-2 text-xs shadow-pop ${
        lado === "direita" ? "left-full ml-2" : "right-full mr-2"
      }`}
    >
      <div className="text-ink-3 mb-1.5 whitespace-nowrap">{titulo}</div>
      <div className="space-y-1">
        {linhas.map((l) => (
          <div key={l.rotulo} className="flex items-center gap-2 whitespace-nowrap">
            <span className="w-2.5 h-[2px] rounded-full shrink-0" style={{ background: l.cor }} />
            <span className="text-ink font-semibold tabular-nums">{l.valor}</span>
            <span className="text-ink-3">{l.rotulo}</span>
          </div>
        ))}
        {total && (
          <div className="flex items-center gap-2 whitespace-nowrap pt-1 mt-1 border-t border-rule">
            <span className="w-2.5 shrink-0" />
            <span className="text-ink font-semibold tabular-nums">{total}</span>
            <span className="text-ink-3">total</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Mensagens

export function AbaMensagens({ d }: { d: AnaliseDetalhada }) {
  const m = d.mensagens;
  const enviadas = m.enviadas_ia + m.enviadas_corretor;
  const semRecibos = m.recibos_desde === null;
  const abordadas = m.abordagens.reduce((s, a) => s + a.quantos, 0);

  // Hora de pico: onde o lead mais escreve. Serve para saber quando o
  // corretor precisa estar por perto e quando o follow-up deve sair.
  const pico = m.por_hora.reduce((a, b) => (b.lead > a.lead ? b : a), m.por_hora[0]!);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi
          icone={Inbox}
          rotulo="Recebidas"
          valor={m.recebidas}
          ajuda="Mensagens escritas pelos leads."
        />
        <Kpi
          icone={Send}
          rotulo="Enviadas"
          valor={enviadas}
          ajuda={`${m.enviadas_ia} da IA, ${m.enviadas_corretor} do corretor. Cada balão conta.`}
        />
        <Kpi
          icone={Reply}
          rotulo="Taxa de retorno"
          valor={pct(m.conversas_com_resposta, m.conversas_com_envio)}
          ajuda={`${m.conversas_com_resposta} de ${m.conversas_com_envio} conversas em que falamos tiveram resposta do lead depois.`}
          tom="ok"
        />
        <Kpi
          icone={Timer}
          rotulo="Lead demora para voltar"
          valor={minutos(m.retorno_mediana_min)}
          ajuda="Mediana, da nossa última fala até a resposta dele."
        />
      </div>

      <section>
        <Titulo nota="Quem escreveu, dia a dia. Um dia de muito lead e pouca IA é a IA parada; muita IA e pouco lead é gente que não está respondendo.">
          Mensagens por dia
        </Titulo>
        <Card className="p-5">
          <BarrasEmPilha
            linhas={m.por_dia.map((x) => ({
              chave: x.dia,
              valores: { lead: x.lead, ia: x.ia, corretor: x.corretor },
            }))}
            series={[
              { id: "lead", rotulo: "Lead", cor: SERIE.azul },
              { id: "ia", rotulo: "IA", cor: SERIE.laranja },
              { id: "corretor", rotulo: "Corretor", cor: SERIE.verde },
            ]}
            rotulo={dm}
            vazio="Nenhuma mensagem no período."
          />
        </Card>
      </section>

      <div className="grid md:grid-cols-2 gap-3">
        <Card className="p-5">
          <h3 className="text-sm font-semibold text-ink">Entrega no WhatsApp</h3>
          {semRecibos ? (
            <p className="text-xs text-ink-3 mt-2">
              Ainda não chegou nenhum recibo de entrega. Os números aparecem
              assim que o WhatsApp confirmar a primeira mensagem.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <Taxa
                  icone={CheckCheck}
                  rotulo="Entregues"
                  parte={m.entregues}
                  todo={m.saidas_medidas}
                />
                <Taxa icone={Eye} rotulo="Lidas" parte={m.lidas} todo={m.saidas_medidas} />
              </div>
              <p className="text-[11px] text-ink-3 mt-4 leading-relaxed">
                Sobre {m.saidas_medidas} mensagens nossas enviadas desde{" "}
                {new Date(m.recibos_desde!).toLocaleDateString("pt-BR")}, quando
                os recibos começaram a ser gravados. "Lida" só aparece para quem
                deixa a confirmação de leitura ligada, então fica abaixo do real.
                Entrega baixa é sinal de número bloqueado.
              </p>
            </>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="text-sm font-semibold text-ink">Velocidade da IA</h3>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <div className="flex items-center gap-1.5 text-xs text-ink-3">
                <Zap size={13} /> Responde em
              </div>
              <div className="text-2xl font-semibold tracking-tight text-ink mt-1">
                {segundos(m.resposta_ia_mediana_s)}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-xs text-ink-3">
                <Timer size={13} /> Pico de leads
              </div>
              <div className="text-2xl font-semibold tracking-tight text-ink mt-1">
                {pico.lead > 0 ? `${pico.hora}h` : "—"}
              </div>
            </div>
          </div>
          <p className="text-[11px] text-ink-3 mt-4 leading-relaxed">
            Tempo mediano entre a última mensagem do lead e o primeiro balão da
            IA. Inclui a espera proposital para o lead terminar de digitar.
            Respostas que aguardaram o horário de atendimento ficam de fora.
          </p>
        </Card>
      </div>

      <section>
        <Titulo nota="Mensagens de lead por hora do dia, no fuso da imobiliária. Mostra quando vale ter um corretor disponível.">
          Quando os leads escrevem
        </Titulo>
        <Card className="p-5">
          <BarrasEmPilha
            linhas={m.por_hora.map((h) => ({
              chave: String(h.hora),
              valores: { lead: h.lead },
            }))}
            series={[{ id: "lead", rotulo: "Mensagens de lead", cor: SERIE.azul }]}
            rotulo={(h) => `${h}h`}
            altura={120}
            vazio="Nenhuma mensagem de lead no período."
          />
        </Card>
      </section>

      {abordadas > 0 && (
        <div className="grid md:grid-cols-2 gap-3">
          <Card className="p-5">
            <h3 className="text-sm font-semibold text-ink mb-4">
              Abordagem de formulário
            </h3>
            <Ranking
              itens={m.abordagens.map((a) => ({
                rotulo: ROTULO_ABORDAGEM[a.status] ?? a.status,
                valor: a.quantos,
              }))}
              vazio="Nenhum formulário no período."
            />
            <p className="text-[11px] text-ink-3 mt-4">
              {pct(
                m.abordagens.find((a) => a.status === "enviada")?.quantos ?? 0,
                abordadas,
              )}{" "}
              dos {abordadas} formulários viraram primeira mensagem.
            </p>
          </Card>
          <Card className="p-5">
            <h3 className="text-sm font-semibold text-ink mb-4">Por que não saiu</h3>
            <Ranking
              itens={m.motivos_recusa.map((x) => ({ rotulo: x.motivo, valor: x.quantos }))}
              vazio="Todas as abordagens saíram."
            />
          </Card>
        </div>
      )}
    </div>
  );
}

function Taxa({
  icone: Icone,
  rotulo,
  parte,
  todo,
}: {
  icone: LucideIcon;
  rotulo: string;
  parte: number;
  todo: number;
}) {
  const v = todo > 0 ? (100 * parte) / todo : 0;
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs text-ink-3">
        <Icone size={13} /> {rotulo}
      </div>
      <div className="flex items-baseline gap-2 mt-1">
        <span className="text-2xl font-semibold tracking-tight text-ink">{pct(parte, todo)}</span>
        <span className="text-xs text-ink-3 tabular-nums">{parte}</span>
      </div>
      <div className="h-1.5 rounded-full bg-surface-3 mt-2 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${v}%`, background: SERIE.azul }} />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Follow-up

export function AbaFollowup({ d }: { d: AnaliseDetalhada }) {
  const f = d.followup;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icone={Send} rotulo="Toques enviados" valor={f.enviados} ajuda={`${f.agendados_agora} agendados para sair.`} />
        <Kpi
          icone={Reply}
          rotulo="Responderam"
          valor={pct(f.responderam, f.enviados)}
          ajuda={`${f.responderam} leads escreveram em até 48h depois do toque.`}
          tom="ok"
        />
        <Kpi
          icone={CalendarCheck}
          rotulo="Agendaram depois"
          valor={f.agendaram_depois}
          ajuda="Marcaram horário em até 7 dias depois de um toque."
        />
        <Kpi
          icone={AlertTriangle}
          rotulo="Falharam ou pulados"
          valor={f.falhos + f.pulados}
          ajuda={`${f.falhos} falharam ao enviar, ${f.pulados} foram pulados na hora de sair.`}
          tom={f.falhos > 0 ? "danger" : undefined}
        />
      </div>

      <section>
        <Titulo nota="Se o terceiro toque quase nunca traz resposta, ele só gasta a paciência do lead (e o risco de bloqueio do número).">
          Resposta por toque
        </Titulo>
        <Card className="p-0 overflow-x-auto">
          <TabelaTaxa
            cabecalho="Toque"
            linhas={f.por_tentativa.map((x) => ({
              rotulo: `${x.tentativa}º toque`,
              enviados: x.enviados,
              responderam: x.responderam,
            }))}
            vazio="Nenhum toque enviado no período."
          />
        </Card>
      </section>

      <div className="grid md:grid-cols-2 gap-3">
        <section>
          <Titulo>Por etapa do funil</Titulo>
          <Card className="p-0 overflow-x-auto">
            <TabelaTaxa
              cabecalho="Etapa quando saiu"
              linhas={f.por_etapa.map((x) => ({
                rotulo: ROTULO_ETAPA[x.etapa] ?? x.etapa,
                enviados: x.enviados,
                responderam: x.responderam,
              }))}
              vazio="Nenhum toque enviado no período."
            />
          </Card>
        </section>
        <section>
          <Titulo>Por que foram cancelados</Titulo>
          <Card className="p-5">
            <Ranking
              itens={f.cancelados.map((x) => ({ rotulo: x.motivo, valor: x.quantos }))}
              vazio="Nenhum toque cancelado no período."
            />
            <p className="text-[11px] text-ink-3 mt-4">
              Cancelar é o normal: o lead respondeu antes, agendou ou pediu para
              parar. Motivo estranho aqui é o que vale investigar.
            </p>
          </Card>
        </section>
      </div>
    </div>
  );
}

function TabelaTaxa({
  cabecalho,
  linhas,
  vazio,
}: {
  cabecalho: string;
  linhas: { rotulo: string; enviados: number; responderam: number }[];
  vazio: string;
}) {
  if (linhas.length === 0) return <p className="text-xs text-ink-3 p-5">{vazio}</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-ink-3 text-xs border-b border-rule bg-surface-2/50">
          <th className="text-left font-medium px-4 py-3">{cabecalho}</th>
          <th className="text-right font-medium px-4 py-3">Enviados</th>
          <th className="text-right font-medium px-4 py-3">Responderam</th>
          <th className="text-left font-medium px-4 py-3 w-2/5">Taxa</th>
        </tr>
      </thead>
      <tbody>
        {linhas.map((l) => {
          const v = l.enviados > 0 ? (100 * l.responderam) / l.enviados : 0;
          return (
            <tr key={l.rotulo} className="border-b border-rule last:border-0">
              <td className="px-4 py-2.5 text-ink-2">{l.rotulo}</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-ink-2">{l.enviados}</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-ink">{l.responderam}</td>
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-surface-3 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${v}%`, background: SERIE.azul }} />
                  </div>
                  <span className="text-xs text-ink tabular-nums w-9 text-right">
                    {Math.round(v)}%
                  </span>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ------------------------------------------------------------------- Corretor

export function AbaCorretor({ d }: { d: AnaliseDetalhada }) {
  const e = d.escalonamentos;
  const avaliados = e.justos + e.injustos;
  const ROTULO_URGENCIA: Record<string, string> = {
    alta: "Alta",
    normal: "Normal",
    baixa: "Baixa",
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi
          icone={Hand}
          rotulo="IA chamou o corretor"
          valor={e.total}
          ajuda={`${pct(e.total, d.ia.respostas)} das respostas da IA terminaram em escalonamento.`}
        />
        <Kpi
          icone={CheckCheck}
          rotulo="Assumidos"
          valor={pct(e.assumidos, e.total)}
          ajuda={`${e.assumidos} de ${e.total}. O resto ficou esperando ou voltou para a IA.`}
          tom={e.total > 0 && e.assumidos < e.total ? "warm" : "ok"}
        />
        <Kpi
          icone={Timer}
          rotulo="Tempo até assumir"
          valor={minutos(e.assumir_mediana_min)}
          ajuda="Mediana, do pedido da IA até alguém clicar em assumir."
        />
        <Kpi
          icone={ThumbsUp}
          rotulo="Valeu a interrupção"
          valor={avaliados > 0 ? pct(e.justos, avaliados) : "—"}
          ajuda={
            avaliados > 0
              ? `${e.justos} de ${avaliados} avaliados pelo corretor.`
              : "Nenhum escalonamento avaliado ainda."
          }
        />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <section>
          <Titulo nota="Motivo repetido é conhecimento que falta na base: a IA chama o corretor para responder o que poderia ter respondido sozinha.">
            Por que chamou
          </Titulo>
          <Card className="p-5">
            <Ranking
              itens={e.motivos.map((x) => ({ rotulo: x.motivo, valor: x.quantos }))}
              vazio="A IA não chamou o corretor no período."
            />
          </Card>
        </section>
        <section>
          <Titulo>Urgência</Titulo>
          <Card className="p-5">
            <Ranking
              itens={e.por_urgencia.map((x) => ({
                rotulo: ROTULO_URGENCIA[x.urgencia] ?? x.urgencia,
                valor: x.quantos,
              }))}
              vazio="A IA não chamou o corretor no período."
            />
          </Card>
        </section>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------ IA

/**
 * O topo da aba de IA: custo diário por modelo e o que a IA fez.
 *
 * O gasto por dia com aquecimento e simulador, e o custo por componente,
 * continuam logo abaixo (vêm de `/api/analise`). Aqui entra o corte por
 * modelo, que é o que mostra o efeito de trocar Opus por Sonnet no dia em
 * que a troca aconteceu.
 */
export function IaTopo({ d }: { d: AnaliseDetalhada }) {
  const ia = d.ia;
  const modelos = [...new Set(ia.custo_por_dia.map((x) => x.modelo))].sort();
  // Cor fixa por modelo, não pela ordem em que aparece no período.
  const corDo = (m: string) =>
    m === "claude-opus-5" ? SERIE.azul : m === "claude-sonnet-5" ? SERIE.laranja : SERIE.verde;

  // Todos os dias do período, inclusive os sem gasto: buraco no eixo
  // pareceria dado faltando.
  const dias = d.mensagens.por_dia.map((x) => x.dia);
  const porDia = new Map<string, Record<string, number>>();
  for (const x of ia.custo_por_dia) {
    const v = porDia.get(x.dia) ?? {};
    v[x.modelo] = (v[x.modelo] ?? 0) + x.usd;
    porDia.set(x.dia, v);
  }

  const usos = ia.ferramentas.reduce((s, f) => s + f.usos, 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi
          icone={Repeat}
          rotulo="Respostas da IA"
          valor={ia.respostas}
          ajuda={`Duração mediana de ${ia.duracao_mediana_ms !== null ? (ia.duracao_mediana_ms / 1000).toFixed(1).replace(".", ",") : "—"} s por resposta.`}
        />
        <Kpi
          icone={VolumeX}
          rotulo="Ficou em silêncio"
          valor={ia.silencios}
          ajuda="Decidiu não mandar nada: lead encerrou, pediu para parar, ou a conversa estava com o corretor."
        />
        <Kpi
          icone={Hand}
          rotulo="Escalou"
          valor={ia.escalaram}
          ajuda={`${pct(ia.escalaram, ia.respostas)} das respostas.`}
        />
        <Kpi
          icone={AlertTriangle}
          rotulo="Com erro"
          valor={ia.com_erro}
          ajuda={`${pct(ia.com_erro, ia.respostas)} das respostas. Detalhes no Rastro de cada conversa.`}
          tom={ia.com_erro > 0 ? "danger" : "ok"}
        />
      </div>

      <section>
        <Titulo nota="Só respostas e toques de follow-up, por modelo. Aquecimento do cache e simulador aparecem no gráfico de gasto logo abaixo.">
          Custo por dia, por modelo
        </Titulo>
        <Card className="p-5">
          <BarrasEmPilha
            linhas={dias.map((dia) => ({ chave: dia, valores: porDia.get(dia) ?? {} }))}
            series={modelos.map((m) => ({
              id: m,
              rotulo: ROTULO_MODELO[m] ?? m,
              cor: corDo(m),
            }))}
            rotulo={dm}
            formato={(n) => `US$ ${n > 0 && n < 0.1 ? n.toFixed(3) : n.toFixed(2)}`}
            vazio="Nenhuma resposta da IA no período."
          />
        </Card>
      </section>

      <section>
        <Titulo nota="Cada ferramenta usada é uma chamada a mais para o modelo, e é isso que mais pesa no custo de uma resposta.">
          Ferramentas usadas
        </Titulo>
        <Card className="p-0 overflow-x-auto">
          {ia.ferramentas.length === 0 ? (
            <p className="text-xs text-ink-3 p-5">Nenhuma ferramenta usada no período.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-ink-3 text-xs border-b border-rule bg-surface-2/50">
                  <th className="text-left font-medium px-4 py-3">Ferramenta</th>
                  <th className="text-right font-medium px-4 py-3">Usos</th>
                  <th className="text-right font-medium px-4 py-3">Em % das respostas</th>
                  <th className="text-left font-medium px-4 py-3 w-1/3">Parcela dos usos</th>
                </tr>
              </thead>
              <tbody>
                {ia.ferramentas.map((f) => (
                  <tr key={f.nome} className="border-b border-rule last:border-0">
                    <td className="px-4 py-2.5 text-ink-2">
                      {ROTULO_FERRAMENTA[f.nome] ?? f.nome}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-ink">{f.usos}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-ink-2">
                      {pct(f.turnos, ia.respostas)}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="h-1.5 rounded-full bg-surface-3 overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${(100 * f.usos) / usos}%`, background: SERIE.azul }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </section>
    </div>
  );
}
